import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2 } from "lucide-react";
import {
  getEtegDashboardOverview,
  type EtegModelConsumption,
} from "@/services/apiCallsService";
import { cn } from "@/lib/utils";

interface ModelConsumptionTableProps {
  startDate: Date;
  endDate: Date;
  clientId?: string | null;
  productId?: string | null;
}

function formatTokens(value: number): string {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}K`;
  }
  return value.toLocaleString("pt-BR");
}

function formatCost(value: number): string {
  return Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function VolumeBar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <span className="text-sm tabular-nums w-16">{formatTokens(value)}</span>
      <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full bg-primary/70"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export function ModelConsumptionTable({
  startDate,
  endDate,
  clientId,
  productId,
}: ModelConsumptionTableProps) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<EtegModelConsumption[]>([]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const response = await getEtegDashboardOverview({
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          client_id:
            clientId && clientId !== "all"
              ? clientId === "sandbox"
                ? "null"
                : clientId
              : undefined,
          product_id: productId && productId !== "all" ? productId : undefined,
        });

        if (cancelled) return;

        if (!response.success || !response.data) {
          setRows([]);
          return;
        }

        setRows(response.data.by_model || []);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, clientId, productId]);

  const maxCalls = rows.reduce((acc, row) => Math.max(acc, row.calls), 0);
  const maxTokens = rows.reduce(
    (acc, row) => Math.max(acc, row.total_tokens),
    0,
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Consumo por Modelo de IA</CardTitle>
        <CardDescription>
          Ordenado por volume de acionamentos no período
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            Carregando modelos...
          </div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground px-4">
            Sem dados de consumo por modelo no período selecionado.
          </div>
        ) : (
          <div className="rounded-md border-t overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Modelo</TableHead>
                  <TableHead>Acionamentos totais</TableHead>
                  <TableHead>Tokens totais</TableHead>
                  <TableHead className="text-right">Custo total</TableHead>
                  <TableHead className="text-right">Taxa de erro</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={`${row.model_id || "x"}-${row.model_name}`}>
                    <TableCell className="font-medium">
                      {row.model_name}
                    </TableCell>
                    <TableCell>
                      <VolumeBar value={row.calls} max={maxCalls} />
                    </TableCell>
                    <TableCell>
                      <VolumeBar value={row.total_tokens} max={maxTokens} />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCost(row.cost)}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "text-right tabular-nums",
                        row.error_rate > 5 && "text-destructive font-medium",
                      )}
                    >
                      {row.error_rate.toLocaleString("pt-BR", {
                        maximumFractionDigits: 1,
                      })}
                      %
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
